import { Component, OnInit, OnDestroy, ViewChild, SimpleChanges, ChangeDetectorRef, HostListener, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PackagesPersoService } from 'src/services/packages-perso.service';
import { ContenuSessionService } from 'src/services/contenu.session.service';
import { ScormPackagePersoPilotageModel } from 'src/models/pilotage/scormPackagePersoPilotageModel';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { MatDialog } from '@angular/material/dialog';
import { CreatePackagePersoComponent } from './create-package-perso/create-package-perso.component';
import { Router } from '@angular/router';
import { DeletePackagePersoDialogComponent } from './delete-package-perso-dialog/delete-package-perso-dialog.component';
import { ConfirmationTogglePackageDialogComponent } from './confirmation-toggle-package-dialog/confirmation-toggle-package-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { StatePersistenceService } from 'src/services/state-persistence.service';
import { PackagesPersoManagmentState, validatePackagesPersoManagmentState } from 'src/models/packages-perso-managment-state.model';
import { ContexteUtilisateur, EntiteModel } from 'src/models/pilotage/contextePilotageUtilisateurModel';

@Component({
    selector: 'app-packages-perso-managment',
    imports: [
        CommonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        FormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginator,
        MatSortModule,
        MatTooltipModule
    ],
    providers: [
        {
            provide: MatPaginatorIntl,
            useClass: CustomPaginatorIntlService
        },
        {
            provide: LOCALE_ID,
            useValue: 'fr-CA'
        }
    ],
    templateUrl: './packages-perso-managment.component.html'
})
export class PackagesPersoManagmentComponent implements OnInit, OnDestroy {
  packages: ScormPackagePersoPilotageModel[] = [];
  filteredPackages: ScormPackagePersoPilotageModel[] = [];
  filteredPackagesAffiches: ScormPackagePersoPilotageModel[] = [];
  isLoading = true;
  searchTerm = '';
  selectedEntities: string[] = [];
  entities: string[] = [];
  filter: 'all' | 'active' | 'inactive' | 'draft' = 'active';
  entityFilterCtrl: FormControl = new FormControl();
  filteredEntities: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  private _onDestroy = new Subject<void>();

  displayedColumns: string[] = [
    'nom',
    'entreprise',
    'statut',
    'dateCreation',
    'actions'
  ];

  pageIndex: number = 0;
  pageSize: number = 10;
  currentPage: number = 0;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  dataSource!: MatTableDataSource<ScormPackagePersoPilotageModel>;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  private readonly COMPONENT_KEY = 'packages-perso-managment';

  readonly DEMO_CONTENT_LIMIT = 2;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(private packagesPersoService: PackagesPersoService,
    private changeDetectorRef: ChangeDetectorRef,
    private utilisateurService: UtilisateurService,
    private translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private contenuSessionService: ContenuSessionService,
    private statePersistenceService: StatePersistenceService) {
    this.dataSource = new MatTableDataSource<ScormPackagePersoPilotageModel>([]);
  }

  ngOnInit(): void {
    this.utilisateurService.obtenirContexte().subscribe(
      (contexte) => {
        this.utilisateurService.contexteUtilisateur = contexte;
        this.entities = this.getAccessibleEntityNames(contexte);
        this.loadPackages();
        this.setupEntityFilter();
      },
      (error) => {
        console.error('Error loading user context:', error);
        this.isLoading = false;
      }
    );
  }

  ngOnDestroy(): void {
    this.saveState();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngAfterViewInit() {
    if (this.sort) {
      this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.totalItems = this.packages.length;
      this.refreshDataSource();
    }
  }

  toggleMenu(index: number): void {
    const realIndex = this.pageIndex * this.pageSize + index;
    this.filteredPackages.forEach((pack, i) => {
      pack.isMenuOpen = (i === realIndex) ? !pack.isMenuOpen : false;
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideMenu = target.closest('.menu-container');
    if (!isInsideMenu) {
      this.filteredPackages.forEach(pack => pack.isMenuOpen = false);
    }
  }

  paginer(event?: PageEvent) {
    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
    }
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.filteredPackagesAffiches = this.filteredPackages.slice(startIndex, endIndex);
    this.dataSource.data = this.filteredPackagesAffiches;
    this.changeDetectorRef.detectChanges();
    this.saveState();
  }

  sortDataChange(sortState: Sort) {
    const data = this.filteredPackages.slice();

    if (!sortState.active || sortState.direction === '') {
      this.sortColumn = '';
      this.sortDirection = '';
      this.filteredPackages = [...this.packages.filter(pkg => {
        const entityMatch = !this.selectedEntities.length ||
          this.obtenirNomEntites(pkg.entiteIds).some(entite => this.selectedEntities.includes(entite));
        const searchTerm = this.searchTerm.toLowerCase();
        const searchMatch = !this.searchTerm ||
          pkg.nomFR.toLowerCase().includes(searchTerm) ||
          pkg.nomEN.toLowerCase().includes(searchTerm) ||
          pkg.nomES.toLowerCase().includes(searchTerm);

        const statusMatch = (
          this.filter === 'all' ||
          (this.filter === 'active' && pkg.actif) ||
          (this.filter === 'inactive' && !pkg.actif && !pkg.isDraft) ||
          (this.filter === 'draft' && !!pkg.isDraft)
        );

        return entityMatch && searchMatch && statusMatch;
      })];
      this.paginer();
      this.saveState();
      return;
    }

    this.sortColumn = sortState.active;
    this.sortDirection = sortState.direction;

    this.filteredPackages = data.sort((a, b) => {
      const isAsc = sortState.direction === 'asc';

      switch (sortState.active) {
        case 'nom':
          const nomA = this.getNomLocalise(a);
          const nomB = this.getNomLocalise(b);
          return this.compareStrings(nomA, nomB, isAsc);

        case 'entreprise':
          const entitesA = this.obtenirNomEntites(a.entiteIds).join(', ');
          const entitesB = this.obtenirNomEntites(b.entiteIds).join(', ');
          return this.compareStrings(entitesA, entitesB, isAsc);

        case 'statut':
          const statutA = a.actif ? 'Actif' : 'Inactif';
          const statutB = b.actif ? 'Actif' : 'Inactif';
          return this.compareStrings(statutA, statutB, isAsc);

        case 'dateCreation':
          const dateA = a.dateCreation ? new Date(a.dateCreation).getTime() : 0;
          const dateB = b.dateCreation ? new Date(b.dateCreation).getTime() : 0;
          return this.compareNumbers(dateA, dateB, isAsc);

        default:
          return 0;
      }
    });

    this.paginer();
  }

  private compareStrings(a: string, b: string, isAsc: boolean): number {
    const valueA = (a || '').toLowerCase().trim();
    const valueB = (b || '').toLowerCase().trim();

    const result = valueA.localeCompare(valueB, 'fr', {
      numeric: true,
      sensitivity: 'base',
      ignorePunctuation: true
    });

    return isAsc ? result : -result;
  }

  private compareNumbers(a: number, b: number, isAsc: boolean): number {
    const result = a - b;
    return isAsc ? result : -result;
  }

  private updatePaginatorLength(): void {
    if (this.paginator) {
      this.paginator.length = this.filteredPackages.length;
      this.totalItems = this.filteredPackages.length;
      this.paginator.page.emit();
      this.changeDetectorRef.detectChanges();
    }
  }


  refreshDataSource(): void {
    this.filteredPackages = this.packages;
    this.dataSource.data = this.filteredPackages;
    this.updatePaginatorLength();
  }



  private setDefaultFilters(): void {

  }

  private getAccessibleEntityNames(contexte: ContexteUtilisateur): string[] {
    const accessibleEntities = contexte.entitesAccessibles ?? [];
    const names = accessibleEntities.map((entite: EntiteModel) => entite.nom);

    return names.sort((a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }

  private removeOutOfScopeSelectedEntities(): void {
    this.selectedEntities = this.selectedEntities.filter(nom => this.entities.includes(nom));
  }

  private setupEntityFilter(): void {
    this.filteredEntities.next(this.entities.slice());
    this.entityFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterEntities();
      });
  }

  private filterEntities(): void {
    if (!this.entities) return;

    let search = this.entityFilterCtrl.value;
    if (!search) {
      this.filteredEntities.next(this.entities.slice());
      return;
    }

    search = search.toLowerCase();
    this.filteredEntities.next(
      this.entities.filter(entity => entity.toLowerCase().includes(search))
    );
  }

  loadPackages(): void {
    this.packagesPersoService.obtenirPackagePersos().subscribe(
      (packages) => {
        this.packages = packages;
        this.filteredPackages = [...this.packages];
        this.loadState();
        this.removeOutOfScopeSelectedEntities();
        this.applyFilters();
        this.applyDefaultSort();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading packages:', error);
        this.isLoading = false;
      }
    );
  }


  setFilter(newFilter: 'all' | 'active' | 'inactive' | 'draft'): void {
    this.filter = newFilter;
    this.pageIndex = 0;
    this.applyFilters();
  }

  applyFilters(): void {
    this.filteredPackages = this.packages.filter(pkg => {
      const entityMatch = !this.selectedEntities.length ||
        this.obtenirNomEntites(pkg.entiteIds).some(entite => this.selectedEntities.includes(entite));
      const searchTerm = this.searchTerm.toLowerCase();
      const searchMatch = !this.searchTerm ||
        pkg.nomFR.toLowerCase().includes(searchTerm) ||
        pkg.nomEN.toLowerCase().includes(searchTerm) ||
        pkg.nomES.toLowerCase().includes(searchTerm);

      const statusMatch = (
        this.filter === 'all' ||
        (this.filter === 'active' && pkg.actif) ||
        (this.filter === 'inactive' && !pkg.actif && !pkg.isDraft) ||
        (this.filter === 'draft' && !!pkg.isDraft)
      );

      return entityMatch && searchMatch && statusMatch;
    });
    this.totalItems = this.filteredPackages.length;
    this.paginer();
    this.saveState();
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.selectedEntities = [];
    this.filter = 'active';
    this.sortColumn = '';
    this.sortDirection = '';
    this.pageIndex = 0;
    this.applyFilters();

    if (this.sort) {
      this.sort.active = '';
      this.sort.direction = '';
      this.sort._stateChanges.next();
    }
  }


  getNomLocalise(pkg: ScormPackagePersoPilotageModel): string {
    const currentLang = this.translate.currentLang || 'fr';
    let nom = '';
    switch (currentLang) {
      case 'fr':
        nom = pkg.nomFR;
        break;
      case 'en':
        nom = pkg.nomEN;
        break;
      case 'es':
        nom = pkg.nomES;
        break;
      default:
        nom = pkg.nomFR;
        break;
    }
    if (nom && nom.trim()) {
      return nom;
    }
    if (pkg.nomFR && pkg.nomFR.trim()) return pkg.nomFR;
    if (pkg.nomEN && pkg.nomEN.trim()) return pkg.nomEN;
    if (pkg.nomES && pkg.nomES.trim()) return pkg.nomES;
    return '';
  }

  obtenirNomEntites(entiteIds: number[]): string[] {
    if (entiteIds && entiteIds.length > 0) {
      return entiteIds.map(id => {
        const entite = this.utilisateurService.contexteUtilisateur.toutesLesEntites.find(e => e.entiteId === id);
        return entite ? entite.nom : '';
      });
    }
    return [''];
  }

  openAddPackage(): void {
    if (this.estCreationBloquee) {
      this.snackBar.open(
        this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CREATION_LIMIT_ERROR', { limite: this.DEMO_CONTENT_LIMIT }),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 5000 }
      );

      return;
    }

    this.router.navigate(['/scorm-perso-managment/create']);
  }

  openEditPackage(packagePerso: ScormPackagePersoPilotageModel): void {
    if (packagePerso.isDraft) {
      this.router.navigate(['/scorm-perso-managment/create', packagePerso.scormPackageId]);
    } else {
      this.router.navigate(['/scorm-perso-managment/edit', packagePerso.scormPackageId]);
    }
  }

  openDeleteDialog(packageId: number): void {
    this.contenuSessionService.hasSessionWithPackage(packageId).subscribe({
      next: (hasSession: boolean) => {
        if (hasSession) {
          this.snackBar.open(
            this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DELETE_DISABLED_SESSION_EXISTS'),
            'OK',
            { duration: 5000 }
          );

          return;
        }

        this.openDeleteConfirmation(packageId);
      },
      error: (error) => {
        console.error('Erreur lors de la vérification des sessions:', error);
        this.openDeleteConfirmation(packageId);
      }
    });
  }

  private openDeleteConfirmation(packageId: number): void {
    const dialogRef = this.dialog.open(DeletePackagePersoDialogComponent, {
      data: { packageId }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadPackages();
      }
    });
  }

  get estModeDemo(): boolean {
    return this.utilisateurService.entiteUtilisateurSelectionnee?.estDemo || false;
  }

  get estLimiteContenusAtteinte(): boolean {
    return this.estLimiteContenusAtteintePour(0);
  }

  get estCreationBloquee(): boolean {
    return this.estLimiteContenusAtteinte;
  }

  obtenirTooltipCreation(): string {
    if (!this.estCreationBloquee) {
      return '';
    }

    return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CREATION_LIMIT_TOOLTIP', { limite: this.DEMO_CONTENT_LIMIT });
  }

  estTypeContenuDejaUtilise(packagePerso: ScormPackagePersoPilotageModel): boolean {
    if (!this.estModeDemo || !packagePerso.typeContenu) {
      return false;
    }

    const contenus = this.contenusDepuisDebutDemo(packagePerso.scormPackageId);

    return contenus.some(contenu => contenu.typeContenu === packagePerso.typeContenu);
  }

  estActivationBloquee(packagePerso: ScormPackagePersoPilotageModel): boolean {
    if (packagePerso.actif) {
      return false;
    }

    return this.estLimiteContenusAtteintePour(packagePerso.scormPackageId) || this.estTypeContenuDejaUtilise(packagePerso);
  }

  obtenirTooltipActivation(packagePerso: ScormPackagePersoPilotageModel): string {
    if (!this.estActivationBloquee(packagePerso)) {
      return '';
    }

    if (this.estLimiteContenusAtteintePour(packagePerso.scormPackageId)) {
      return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CONTENT_LIMIT_TOOLTIP', { limite: this.DEMO_CONTENT_LIMIT });
    }

    return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CONTENT_TYPE_TOOLTIP');
  }

  private obtenirMessageActivationBloquee(packagePerso: ScormPackagePersoPilotageModel): string {
    if (this.estLimiteContenusAtteintePour(packagePerso.scormPackageId)) {
      return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CONTENT_LIMIT_ERROR', { limite: this.DEMO_CONTENT_LIMIT });
    }

    return this.translate.instant('PACKAGES_PERSO_MANAGEMENT.DEMO_CONTENT_TYPE_ERROR');
  }

  private estLimiteContenusAtteintePour(scormPackageIdExclu: number): boolean {
    if (!this.estModeDemo) {
      return false;
    }

    return this.contenusDepuisDebutDemo(scormPackageIdExclu).length >= this.DEMO_CONTENT_LIMIT;
  }

  private contenusDepuisDebutDemo(scormPackageIdExclu: number = 0): ScormPackagePersoPilotageModel[] {
    const entite = this.utilisateurService.entiteUtilisateurSelectionnee;

    if (!entite?.dateDebutDemo) {
      return [];
    }

    const dateDebutDemo = new Date(entite.dateDebutDemo);

    return this.packages.filter(packagePerso => {
      if (packagePerso.scormPackageId === scormPackageIdExclu) {
        return false;
      }

      if (!packagePerso.entiteIds?.includes(entite.entiteId)) {
        return false;
      }

      return new Date(packagePerso.dateCreation) >= dateDebutDemo;
    });
  }

  togglePackageActivation(packageId: number, currentStatus: boolean): void {
    const pkg = this.packages.find(p => p.scormPackageId === packageId);
    if (!pkg) return;

    if (this.estActivationBloquee(pkg)) {
      this.snackBar.open(
        this.obtenirMessageActivationBloquee(pkg),
        this.translate.instant('COMMUN.CLOSE'),
        { duration: 5000 }
      );

      return;
    }

    const packageName = this.getNomLocalise(pkg);

    const dialogRef = this.dialog.open(ConfirmationTogglePackageDialogComponent, {
      data: {
        packageId,
        packageName,
        currentStatus
      },
      width: '500px',
      minHeight: '200px'
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadPackages();
      }
    });
  }

  private saveState(): void {
    const state: PackagesPersoManagmentState = {
      selectedEntities: this.selectedEntities,
      filter: this.filter,
      searchTerm: this.searchTerm,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize
    };

    const contextId = this.utilisateurService.entiteUtilisateurSelectionnee?.entiteId;
    this.statePersistenceService.saveState(this.COMPONENT_KEY, state, contextId);
  }

  private loadState(): void {
    const contextId = this.utilisateurService.entiteUtilisateurSelectionnee?.entiteId;
    const savedState = this.statePersistenceService.loadState(this.COMPONENT_KEY, contextId);

    if (savedState) {
      const validatedState = validatePackagesPersoManagmentState(savedState);

      this.selectedEntities = validatedState.selectedEntities;
      this.filter = validatedState.filter;
      this.searchTerm = validatedState.searchTerm;
      this.pageIndex = validatedState.pageIndex;
      this.pageSize = validatedState.pageSize;
    }
  }

  private applyDefaultSort(): void {
    if (!this.sortColumn || !this.sortDirection) {
      this.sortColumn = 'nom';
      this.sortDirection = 'asc';
      if (this.sort) {
        this.sort.active = 'nom';
        this.sort.direction = 'asc';
        this.sort._stateChanges.next();
      }
      this.sortDataChange({ active: 'nom', direction: 'asc' });
    }
  }
}
